import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { TablePagination } from '../../../../../components/Table/TablePagination'
import { FilterManager, IBaseFilters } from '../../../../common/filter/FilterManager'
import { getMerchantLookupWithToken } from '../../../../common/core/_requests'
import { convertToUTCByMSK } from '../../../../../utils'
import { getMoscowDateTime } from '../../../../../utils/getMoscowDateTime'

import { useAuth } from '../../../auth'
import { canUser, matchingCapabilities, MatchingFile } from '../../core/_models'
import DisputesTable from './components/DisputesTable/DisputesTable'
import DateRangeFilter from './components/DateRangeFilter/DateRangeFilter'
import { ResolveSuccessModal } from './components/ResolveSuccessModal/ResolveSuccessModal'
import { ResolveRejectModal } from './components/ResolveRejectModal/ResolveRejectModal'
import { getDisputeList, getDisputeReasonLookup } from './core/api'
import { DisputeRecord, DisputeReasonLookupItem, DisputeType } from './core/response.types'
import { DisputeFilters } from './core/types'
import { DisputeQuery } from './core/query.types'
import { DISPUTES_BREADCRUMBS, DISPUTE_TYPE_OPTIONS, PAGE_SIZE } from './core/consts'

interface InternalFilterState extends Omit<IBaseFilters, 'dateRange'> {
  // search — из IBaseFilters: одно текстовое поле, токен тикета или ID транзакции у мерчанта.
  reasonIds?: string[]
  merchantTokens?: string[]
  types?: string[]
  dateRange?: { from?: Date; to?: Date }
}

// Период по умолчанию: последняя неделя. Фильтр обязан показывать тот диапазон, который реально
// применён к выборке, иначе пустые поля врут — будто ограничения по датам нет.
//
// Границы по канону проекта (см. currentShiftFullDay в DateTimeRangeFilter): считаем от
// московского времени, обе границы в 00:00, верхняя — СЛЕДУЮЩИЙ день. Иначе «по» = «сейчас»
// отрезал бы today-записи, созданные позже текущего часа.
const DEFAULT_RANGE_DAYS = 7
const DAY_MS = 24 * 60 * 60 * 1000

function defaultDateRange(): { from: Date; to: Date } {
  const from = new Date(getMoscowDateTime().getTime() - DEFAULT_RANGE_DAYS * DAY_MS)
  const to = new Date(getMoscowDateTime().getTime() + DAY_MS)

  from.setHours(0, 0, 0, 0)
  to.setHours(0, 0, 0, 0)

  return { from, to }
}

export const DisputesPage: React.FC = () => {
  const { currentUser } = useAuth()
  // Право на операции с чеками — тот же canEditTickets, что в очереди мэтчинга и в
  // завершённых: бэкенд гейтит verify/reject одним списком ролей на всех поверхностях.
  const canManageBills = useMemo(
    () => canUser(currentUser, matchingCapabilities.canEditTickets),
    [currentUser]
  )

  const [items, setItems] = useState<DisputeRecord[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [reasons, setReasons] = useState<DisputeReasonLookupItem[]>([])
  const [merchantOptions, setMerchantOptions] = useState<{ value: string; label: string }[]>([])
  const [appliedQuery, setAppliedQuery] = useState<DisputeFilters>({})
  const [resolving, setResolving] = useState<DisputeRecord | null>(null)
  const [rejecting, setRejecting] = useState<DisputeRecord | null>(null)
  // Один и тот же объект дат идёт и в фильтр (как предзаполнение), и в первый запрос —
  // так подпись под фильтром всегда совпадает с тем, что показано в таблице.
  const initialRange = useMemo(() => defaultDateRange(), [])

  // Deep-link из очереди депозитов: ?paymentTicketToken=…
  //
  // Имя параметра — часть уже разосланных ссылок, поэтому оно осталось прежним, хотя поле поиска
  // теперь принимает и ID транзакции: токен из ссылки просто подставляется в это поле.
  //
  // Параметр — ОДНОРАЗОВАЯ инструкция: он предзаполняет фильтр при открытии и тут же вычищается из
  // URL. Иначе «Сбросить фильтр» возвращал бы токен обратно (FilterManager сбрасывает в
  // defaultValues, а там лежал бы токен) — поле не очищалось бы никогда, и скопированная ссылка
  // после сброса всё равно тащила бы фильтр.
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const tokenFromUrl = useMemo(
    () => new URLSearchParams(search).get('paymentTicketToken') || undefined,
    [search]
  )
  // Первый рендер решает, что грузить; дальше состояние живёт в FilterManager.
  const initialToken = useRef(tokenFromUrl).current
  const initialLoadDone = useRef(false)

  useEffect(() => {
    if (tokenFromUrl) {
      navigate(pathname, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getDisputeReasonLookup()
      .then((r) => {
        if (r.data.success) setReasons(r.data.data ?? [])
      })
      .catch(() => {})

    // lookup-with-token сам скоуплен по мерчантам пользователя, поэтому оператор платежей
    // мерчанта увидит в фильтре ровно тех, кого ему отдаст и журнал.
    getMerchantLookupWithToken()
      .then((r) => {
        if (r.data?.success) setMerchantOptions(r.data.data ?? [])
      })
      .catch(() => setMerchantOptions([]))
  }, [])

  const reasonOptions = useMemo(
    () => reasons.map((r) => ({ value: String(r.value), label: r.label })),
    [reasons]
  )

  const load = useCallback(async (pg: number, query: DisputeFilters) => {
    setLoading(true)
    try {
      const params: DisputeQuery = { ...query, page: pg + 1, perPage: PAGE_SIZE }
      const r = await getDisputeList(params)
      if (r.data.success) {
        setItems(r.data.data ?? [])
        setTotalRows(r.data.totalRows ?? 0)
      } else {
        setItems([])
        setTotalRows(0)
      }
    } catch {
      setItems([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (initialLoadDone.current) {
      return
    }
    initialLoadDone.current = true

    // Deep-link по тикету перекрывает период: ищем конкретный тикет, а не «за неделю».
    const initial: DisputeFilters = initialToken
      ? { search: initialToken }
      : {
          startedAtFrom: convertToUTCByMSK(initialRange.from),
          startedAtTo: convertToUTCByMSK(initialRange.to),
        }

    setAppliedQuery(initial)
    load(0, initial)
  }, [load, initialToken, initialRange])

  const handleApplyFilters = (filters: InternalFilterState) => {
    const query: DisputeFilters = {}

    const search = filters.search?.trim()
    if (search) query.search = search

    // selectMulti отдаёт string[] — приводим к number[] для reasonIds.
    if (filters.reasonIds?.length) {
      query.reasonIds = filters.reasonIds.map(Number).filter((n) => Number.isInteger(n) && n > 0)
    }

    // Токены мерчантов остаются строками — приводить нечего.
    if (filters.merchantTokens?.length) {
      query.merchantTokens = filters.merchantTokens
    }

    if (filters.types?.length) {
      query.types = filters.types.filter((t): t is DisputeType => t === 'DEPOSIT_DISPUTE' || t === 'PAYOUT_DISPUTE')
    }

    if (filters.dateRange?.from) query.startedAtFrom = convertToUTCByMSK(filters.dateRange.from)
    if (filters.dateRange?.to) query.startedAtTo = convertToUTCByMSK(filters.dateRange.to)

    setAppliedQuery(query)
    setPage(0)
    load(0, query)
  }

  const handlePageChange = (selected: number) => {
    setPage(selected)
    load(selected, appliedQuery)
  }

  const reload = () => load(page, appliedQuery)

  // Депозит освобождён — правим строку тем моментом, который приняли мэтчинг и бэкенд: колонка
  // «Освобождение выплаты» и наличие действия считаются из одного поля, и перезапрашивать ради
  // него весь журнал незачем.
  //
  // Признак действия выводим здесь же, потому что из трёх условий бэкенда (связь с выплатой,
  // открытый спор, живое удержание) первые два у этой строки уже выполнены — иначе действия бы не
  // было, — и меняется ровно момент. Остальное придёт со следующей загрузкой списка.
  const updateDisputeHold = useCallback((disputeId: number, heldUntil: string) => {
    setItems((prev) =>
      prev.map((d) =>
        d.id === disputeId
          ? { ...d, chunkHeldUntil: heldUntil, canReleaseChunk: new Date(heldUntil) > new Date() }
          : d
      )
    )
  }, [])

  // Чек прикреплён или проверен — обновляем строку локально, без перезагрузки журнала:
  // ни то, ни другое не меняет ни диспут, ни статус тикета.
  const updateDisputeFiles = useCallback((disputeId: number, files: MatchingFile[]) => {
    setItems((prev) => prev.map((d) => (d.id === disputeId ? { ...d, files } : d)))
  }, [])

  // Строка приходит из ответа бэкенда целиком: смена причины меняет только её, журнал не перезапрашиваем.
  const updateDispute = useCallback((updated: DisputeRecord) => {
    setItems((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)))
  }, [])

  const pageCount = Math.ceil(totalRows / PAGE_SIZE)

  return (
    <>
      <PageTitle breadcrumbs={DISPUTES_BREADCRUMBS as any}>Диспуты</PageTitle>
      <FilterManager<InternalFilterState>
        activeFilters={[
          'dateRange',
          'search',
          { filter: 'selectMulti', fieldName: 'merchantTokens' },
          { filter: 'selectMulti', fieldName: 'reasonIds' },
          { filter: 'selectMulti', fieldName: 'types' },
        ]}
        defaultValues={{
          // На первом рендере сюда попадает токен из ссылки — им инициализируется поле.
          // Дальше URL уже вычищен, значит «Сбросить» вернёт пустое поле, а не токен.
          search: tokenFromUrl ?? '',
          reasonIds: [],
          merchantTokens: [],
          types: [],
          // Пусто при deep-link: там выборка не ограничена периодом, и поля не должны врать.
          dateRange: initialToken ? {} : initialRange,
        }}
        customFilters={{
          dateRange: { component: DateRangeFilter as any, defaultValue: {} as any },
        }}
        filterProps={{
          search: { placeholder: 'Токен или ID транзакции тикета либо виновника' },
          merchantTokens: { options: merchantOptions, label: 'Мерчанты', placeholder: 'Все' },
          reasonIds: { options: reasonOptions, label: 'Причины', placeholder: 'Все' },
          types: { options: DISPUTE_TYPE_OPTIONS, label: 'Тип', placeholder: 'Все' },
          dateRange: { hideRanges: true, className: 'w-100' },
        }}
        onApplyFilters={handleApplyFilters}
      />
      <div className='card'>
        <div className='card-body'>
          <DisputesTable
            items={items}
            loading={loading}
            canManageBills={canManageBills}
            onFilesChange={updateDisputeFiles}
            onResolveSuccess={setResolving}
            onReject={setRejecting}
            onChunkReleased={updateDisputeHold}
            reasons={reasons}
            onDisputeUpdated={updateDispute}
          />
          <TablePagination
            currentPage={page}
            pageCount={pageCount}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
      <ResolveSuccessModal
        show={resolving !== null}
        dispute={resolving}
        reasons={reasons}
        onClose={() => setResolving(null)}
        onResolved={reload}
      />
      <ResolveRejectModal
        show={rejecting !== null}
        dispute={rejecting}
        reasons={reasons}
        onClose={() => setRejecting(null)}
        onResolved={reload}
      />
    </>
  )
}

export default DisputesPage
